import {
    PublicBlendedCourse,
    PublicChildOccurrence,
    PublicClassCourse,
    PublicEducator,
    PublicOccurrence,
} from '@cademy-package/sdk-component-types';
import { FunctionComponent, useCallback, useMemo } from 'react';
import { OccurrenceCard } from './OccurrenceCard';
import { OccurrenceBlockCard } from './OccurrenceBlockCard';
import { getWaitlistUrl } from '../../../services/getWaitlistUrl';
import { useIsMarketplace } from '../../../contexts/AppContext/useIsMarketplace';
import { useIsEmbed } from '../../../contexts/AppContext/useIsEmbed';
import { useRouter } from 'next/navigation';
import { isChildOccurrence } from '../../../services/occurrences/isChildOccurrence';
import { isBlockOccurrence } from '../../../services/occurrences/isBlockOccurrence';

type OccurrenceListItemProps = {
    occurrence: PublicOccurrence;
    course: PublicClassCourse | PublicBlendedCourse;
    educator: PublicEducator;
    onOccurrenceSelected: (occurrence: PublicOccurrence) => void;
    occurrences: Array<PublicOccurrence>;
    showVATLabel: boolean;
    hidePricing?: boolean;
};

export const OccurrenceListItem: FunctionComponent<OccurrenceListItemProps> = ({
    occurrence,
    course,
    educator,
    onOccurrenceSelected,
    occurrences,
    showVATLabel,
    hidePricing,
}) => {
    const router = useRouter();
    const isSoldOut: boolean = useMemo(
        () => occurrence && occurrence.available_spaces === 0,
        [occurrence]
    );
    const isWaitlistEnabled: boolean = useMemo(
        () => 'waitlist_enabled' in course && course.waitlist_enabled === true,
        [course]
    );
    const isMarketplace = useIsMarketplace();
    const isEmbed = useIsEmbed();

    const handleOccurrenceCardClick = useCallback(
        (selectedOccurrence: PublicOccurrence) => {
            if (isSoldOut && isWaitlistEnabled) {
                const waitlistUrl = getWaitlistUrl({
                    course,
                    educator,
                    isMarketplace,
                    occurrence: selectedOccurrence,
                    isEmbed,
                });
                if (isEmbed) {
                    return window.open(waitlistUrl);
                }
                router.push(waitlistUrl);
            }
            if (!isSoldOut) {
                onOccurrenceSelected(selectedOccurrence);
            }
        },
        [
            course,
            educator,
            isMarketplace,
            isSoldOut,
            isWaitlistEnabled,
            onOccurrenceSelected,
            isEmbed,
            router,
        ]
    );

    const handleChildOccurrenceCardClick = useCallback(
        (selectedOccurrence: PublicChildOccurrence) => {
            const isChildSoldOut: boolean =
                selectedOccurrence && selectedOccurrence.available_spaces === 0;
            if (isChildSoldOut && isWaitlistEnabled) {
                const waitlistUrl = getWaitlistUrl({
                    course,
                    educator,
                    isMarketplace,
                    occurrence: selectedOccurrence,
                    isEmbed,
                });
                if (isEmbed) {
                    return window.open(waitlistUrl);
                }
                router.push(waitlistUrl);
            }
            if (!isChildSoldOut) {
                onOccurrenceSelected(selectedOccurrence);
            }
        },
        [course, educator, isMarketplace, isWaitlistEnabled, onOccurrenceSelected, isEmbed, router]
    );

    const handleOccurrenceListItemClick = useCallback(
        (selectedOccurrence: PublicOccurrence) => {
            const isChild = isChildOccurrence(selectedOccurrence);

            if (isChild) {
                handleChildOccurrenceCardClick(selectedOccurrence);
            } else {
                handleOccurrenceCardClick(selectedOccurrence);
            }
        },
        [handleChildOccurrenceCardClick, handleOccurrenceCardClick]
    );

    if (isChildOccurrence(occurrence) && occurrence.individually_bookable !== true) {
        return null;
    }

    if (isBlockOccurrence(occurrence)) {
        return (
            <OccurrenceBlockCard
                occurrence={occurrence}
                course={course}
                occurrenceCardClickHandler={handleOccurrenceListItemClick}
                occurrences={occurrences}
                isSoldOut={isSoldOut}
                isWaitlistEnabled={isWaitlistEnabled}
                showVATLabel={showVATLabel}
                hidePricing={hidePricing}
            />
        );
    }

    return (
        <OccurrenceCard
            occurrence={occurrence}
            course={course}
            occurrenceCardClickHandler={handleOccurrenceCardClick}
            isSoldOut={isSoldOut}
            isWaitlistEnabled={isWaitlistEnabled}
            showVATLabel={showVATLabel}
            hidePricing={hidePricing}
        />
    );
};
